/**
 * @nanokart/tryon-studio-sdk
 * CameraCaptureModal — Robust cross-device camera capture (person & garment modes)
 *
 * Engineering notes:
 * - Prefers the BACK (environment) camera when available, falls back to front.
 * - Never forces portrait resolution constraints: forcing them makes desktop
 *   browsers crop the landscape webcam sensor ("zoomed-in camera" bug).
 * - Viewport adapts to the real video aspect ratio with objectFit: contain,
 *   so the full sensor frame is always visible (no crop = no fake zoom).
 * - Resets hardware zoom to minimum on devices that expose a zoom capability.
 * - Digital zoom slider: CSS preview + real center-crop at capture (WYSIWYG).
 * - Review step (Retake / Use Photo) before the image is accepted.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';

export interface CameraCaptureModalProps {
  /** 'person' = customer full-body photo, 'garment' = live dress capture */
  mode?: 'person' | 'garment';
  onCapture: (file: File) => void;
  onClose: () => void;
}

const MODE_COPY = {
  person: {
    title: 'Take Your Photo',
    guide: 'Stand full-body in frame',
    liveTip: 'Full body (head-to-toe) · Face the camera · Good lighting',
    reviewQuestion: 'Is your full body clearly visible?',
    banner: null as string | null,
    confirmLabel: 'Use Photo',
  },
  garment: {
    title: 'Capture Dress',
    guide: 'Fit the entire dress in frame',
    liveTip: 'Hold steady · Fill the frame with the dress',
    reviewQuestion: 'Is the dress captured properly? All parts visible and clear?',
    banner: 'Place the dress on a clean background with good lighting, and make sure every part of the dress is clearly visible.',
    confirmLabel: 'Use This Dress',
  },
};

function mapCameraError(err: unknown): string {
  const name = (err as { name?: string })?.name ?? '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError')
    return 'Camera access denied. Please allow camera permissions in your browser settings.';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError')
    return 'No camera found on this device.';
  if (name === 'NotReadableError' || name === 'TrackStartError')
    return 'Camera is being used by another application.';
  return 'Could not access camera. Please try again.';
}

export const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  mode = 'person',
  onCapture,
  onClose,
}) => {
  const copy = MODE_COPY[mode];
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Requested facing: back camera preferred on every device that has one
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  // Actual facing reported by the acquired track (drives preview mirroring)
  const [actualFacing, setActualFacing] = useState<'environment' | 'user'>('user');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [zoom, setZoom] = useState(1);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async (preferred: 'environment' | 'user') => {
    setIsLoading(true);
    setError(null);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not supported in this browser.');
      setIsLoading(false);
      return;
    }

    // Constraint cascade: ideal facing + landscape-friendly resolution →
    // plain facing → any camera. Never forces portrait (avoids sensor crop/zoom).
    const attempts: MediaStreamConstraints[] = [
      { video: { facingMode: { ideal: preferred }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
      { video: { facingMode: preferred }, audio: false },
      { video: true, audio: false },
    ];

    let stream: MediaStream | null = null;
    let lastError: unknown = null;
    for (const constraints of attempts) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (err) {
        lastError = err;
        if ((err as { name?: string })?.name === 'NotAllowedError') break;
      }
    }

    if (!stream) {
      setError(mapCameraError(lastError));
      setIsLoading(false);
      return;
    }

    streamRef.current = stream;
    const track = stream.getVideoTracks()[0];

    // Detect the real facing mode (laptops usually report none → treat as front)
    const settings = (track?.getSettings?.() ?? {}) as { facingMode?: string };
    setActualFacing(settings.facingMode === 'environment' ? 'environment' : 'user');

    // Reset hardware zoom to minimum — some devices start zoomed-in
    try {
      const caps = (track?.getCapabilities?.() ?? {}) as { zoom?: { min?: number } };
      if (caps.zoom && typeof caps.zoom.min === 'number') {
        await track.applyConstraints({
          advanced: [{ zoom: caps.zoom.min } as unknown as MediaTrackConstraintSet],
        });
      }
    } catch {
      // best-effort
    }

    const video = videoRef.current;
    if (video) {
      video.srcObject = stream;
      const markReady = () => setIsLoading(false);
      video.onloadedmetadata = () => {
        const p = video.play();
        if (p && typeof p.then === 'function') p.then(markReady).catch(markReady);
        else markReady();
      };
      // Safety net for browsers that never fire loadedmetadata
      window.setTimeout(markReady, 4000);
    }
  }, []);

  useEffect(() => {
    setCapturedImage(null);
    setZoom(1);
    startCamera(facing);
    navigator.mediaDevices?.enumerateDevices?.()
      .then((devices) => setHasMultipleCameras(devices.filter((d) => d.kind === 'videoinput').length > 1))
      .catch(() => {});
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facing, startCamera]);

  const switchCamera = useCallback(() => {
    setCapturedImage(null);
    setFacing((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, []);

  const mirrored = actualFacing === 'user';

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;

    // WYSIWYG: crop exactly the region visible in the portrait viewport
    // (video is rendered with objectFit: cover inside the frame).
    const frameAspect = video.clientWidth && video.clientHeight
      ? video.clientWidth / video.clientHeight
      : 3 / 4;
    const videoAspect = vw / vh;

    let visibleW = vw;
    let visibleH = vh;
    if (videoAspect > frameAspect) {
      // Video wider than the frame → sides are cropped by `cover`
      visibleW = vh * frameAspect;
    } else {
      // Video taller than the frame → top/bottom are cropped by `cover`
      visibleH = vw / frameAspect;
    }

    // Apply the digital zoom as a further center crop
    const sw = visibleW / zoom;
    const sh = visibleH / zoom;
    const sx = (vw - sw) / 2;
    const sy = (vh - sh) / 2;

    const maxDim = 1440;
    const scale = Math.min(1, maxDim / Math.max(sw, sh));
    const tw = Math.round(sw * scale);
    const th = Math.round(sh * scale);
    canvas.width = tw;
    canvas.height = th;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (mirrored) {
      ctx.translate(tw, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, tw, th);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    setCapturedImage(canvas.toDataURL('image/jpeg', 0.9));
  }, [zoom, mirrored]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
  }, []);

  const confirmPhoto = useCallback(async () => {
    if (!capturedImage) return;
    try {
      const resp = await fetch(capturedImage);
      const blob = await resp.blob();
      const file = new File([blob], `${mode}-camera-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      onCapture(file);
      onClose();
    } catch {
      onClose();
    }
  }, [capturedImage, mode, onCapture, onClose]);

  const handleClose = useCallback(() => {
    stopStream();
    setCapturedImage(null);
    onClose();
  }, [stopStream, onClose]);

  const videoStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    // Fill the portrait frame edge-to-edge like a native camera app.
    // Capture crops the exact same visible region (WYSIWYG).
    objectFit: 'cover',
    display: capturedImage ? 'none' : 'block',
    transform: `scale(${zoom})${mirrored ? ' scaleX(-1)' : ''}`,
    transformOrigin: 'center center',
    transition: 'transform 0.1s',
    backgroundColor: '#000',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        animation: 'nk-fade-in 0.2s ease-out',
      }}
      onClick={handleClose}
      data-testid="camera-modal"
      data-camera-mode={mode}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#0f172a', borderRadius: '20px', overflow: 'hidden',
          width: '100%', maxWidth: '460px', maxHeight: '92vh',
          animation: 'nk-scale-in 0.2s ease-out',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>
              {capturedImage ? 'Review Photo' : copy.title}
            </span>
          </div>
          <button
            onClick={handleClose}
            style={{ padding: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="close-camera-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Camera View — fixed portrait frame (3:4), like a native camera app */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3 / 4',
            maxHeight: '68vh',
            backgroundColor: '#000',
            overflow: 'hidden',
            flexShrink: 1,
            minHeight: 260,
          }}
        >
          {/* Loading */}
          {isLoading && !error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, backgroundColor: '#1e293b', zIndex: 6 }}>
              <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.15)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'nk-spin 0.8s linear infinite' }} />
              <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Starting camera...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, backgroundColor: '#1e293b', zIndex: 6 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', margin: 0 }}>{error}</p>
              <button onClick={() => startCamera(facing)} style={{ padding: '8px 20px', borderRadius: 10, background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                Try Again
              </button>
            </div>
          )}

          {/* Video feed */}
          <video ref={videoRef} autoPlay playsInline muted style={videoStyle} />

          {/* Captured image preview — exact portrait crop, fills the frame */}
          {capturedImage && (
            <img src={capturedImage} alt="Captured" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', backgroundColor: '#000' }} />
          )}

          {/* Garment suggestion banner */}
          {copy.banner && !capturedImage && !isLoading && !error && (
            <div
              style={{
                position: 'absolute', top: 10, left: 10, right: 10, zIndex: 5,
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
                borderRadius: 10, padding: '8px 10px',
              }}
              data-testid="garment-camera-tips"
            >
              <span style={{ fontSize: '14px', lineHeight: 1 }}>👗</span>
              <p style={{ margin: 0, color: '#e2e8f0', fontSize: '11px', lineHeight: 1.45 }}>{copy.banner}</p>
            </div>
          )}

          {/* Framing guide overlay */}
          {!capturedImage && !isLoading && !error && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'relative', width: '82%', height: mode === 'garment' ? '78%' : '90%' }}>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 100 100" preserveAspectRatio="none">
                  <rect x="1.5" y="1.5" width="97" height="97" rx="4" ry="4" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.6" />
                </svg>
                {/* Corner brackets drawn separately so they don't stretch */}
                {[
                  { top: 0, left: 0, transform: 'none' },
                  { top: 0, right: 0, transform: 'scaleX(-1)' },
                  { bottom: 0, left: 0, transform: 'scaleY(-1)' },
                  { bottom: 0, right: 0, transform: 'scale(-1,-1)' },
                ].map((pos, i) => (
                  <svg key={i} width="26" height="26" viewBox="0 0 26 26" style={{ position: 'absolute', ...pos }}>
                    <path d="M 2 20 L 2 2 L 20 2" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                ))}
                <div style={{ position: 'absolute', bottom: '6%', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', margin: 0, whiteSpace: 'nowrap' }}>{copy.guide}</p>
                </div>
              </div>
            </div>
          )}

          {/* Camera switch (front/back) */}
          {hasMultipleCameras && !capturedImage && !isLoading && !error && (
            <button
              onClick={switchCamera}
              style={{ position: 'absolute', top: copy.banner ? 56 : 12, right: 12, width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}
              title={actualFacing === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
              data-testid="switch-camera-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                <circle cx="12" cy="12" r="3" />
                <path d="m18 22-3-3 3-3" /><path d="m6 2 3 3-3 3" />
              </svg>
            </button>
          )}

          {/* Zoom slider */}
          {!capturedImage && !isLoading && !error && (
            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.5)', borderRadius: 20, padding: '6px 14px', zIndex: 5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
              <input
                type="range" min="1" max="3" step="0.05" value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ width: 90, cursor: 'pointer', accentColor: '#3b82f6' }}
                data-testid="zoom-slider"
              />
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          {capturedImage ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <p style={{ color: '#cbd5e1', fontSize: '12px', margin: 0, textAlign: 'center' }} data-testid="review-question">
                {copy.reviewQuestion}
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={retakePhoto}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: '#334155', color: '#fff', borderRadius: 14, border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                  data-testid="retake-photo-btn"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-5.18" /></svg>
                  Retake
                </button>
                <button
                  onClick={confirmPhoto}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: '#2563eb', color: '#fff', borderRadius: 14, border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                  data-testid="use-photo-btn"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                  {copy.confirmLabel}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <button
                onClick={capturePhoto}
                disabled={isLoading || !!error}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: '#fff', border: '4px solid #1e293b',
                  cursor: isLoading || !!error ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !!error ? 0.5 : 1,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                data-testid="capture-button"
              >
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fff', border: '3px solid #0f172a' }} />
              </button>
              {!isLoading && !error && (
                <p style={{ color: '#475569', fontSize: '11px', margin: 0, textAlign: 'center' }}>
                  {copy.liveTip}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CameraCaptureModal;
